import type { AdminPlaceDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@upme/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@upme/ui/components/popover';
import { cn } from '@upme/ui/lib/utils';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useState } from 'react';
import { useAdminPlaces } from '../api/places.queries';

interface Props {
  /** cityId фильтр — обязателен для включения. Без него combobox disabled. */
  cityId?: string | undefined;
  value?: string | null | undefined;
  onChange: (id: string | null, place: AdminPlaceDto | null) => void;
}

export function PlaceCombobox({ cityId, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data } = useAdminPlaces(
    cityId ? { cityId, search: search || undefined, isActive: true, limit: 20 } : {},
    { enabled: !!cityId },
  );

  const items = data?.items ?? [];
  const selected = items.find((p) => p.id === value) ?? null;

  const disabled = !cityId;

  function handleSelect(place: AdminPlaceDto) {
    onChange(place.id, place);
    setOpen(false);
    setSearch('');
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(null, null);
  }

  return (
    <Popover open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between"
        >
          <span className="truncate">
            {selected ? selected.name : disabled ? 'Сначала выберите город' : 'Выберите место (опц.)'}
          </span>
          <span className="ml-2 flex items-center gap-1 shrink-0">
            {value && !disabled && (
              <X
                className="h-3.5 w-3.5 opacity-50 hover:opacity-100"
                onClick={handleClear}
                aria-label="Очистить"
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[320px]">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Поиск места..." value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>Не найдено</CommandEmpty>
            <CommandGroup>
              {items.map((place) => (
                <CommandItem key={place.id} value={place.id} onSelect={() => handleSelect(place)}>
                  <Check className={cn('mr-2 h-4 w-4', value === place.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{place.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground truncate max-w-[100px]">
                    {place.addressText}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
